import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Separator } from "@/components/ui/separator";
import { RefreshCw, Server, Scale, Clock, CheckCircle2, XCircle, AlertTriangle, Wrench } from "lucide-react";
import api, { errMsg } from "../lib/api";
import { formatRupiah, formatDateTime, maskAccount } from "../lib/format";

export default function DevTools() {
  const [overview, setOverview] = useState(null);
  const [pending, setPending] = useState(null);
  const [busy, setBusy] = useState("");

  const load = useCallback(async () => {
    try {
      const [o, p] = await Promise.all([api.get("/dev/overview"), api.get("/dev/pending-transfers")]);
      setOverview(o.data);
      setPending(p.data);
    } catch (err) {
      toast.error(errMsg(err, "Gagal memuat data dev tools"));
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const settle = async (txnId, status) => {
    setBusy(txnId + status);
    try {
      await api.post(`/dev/transfers/${txnId}/settle`, { status });
      toast.success(status === "SUCCESS" ? "Transfer diselesaikan (SUKSES)" : "Transfer digagalkan, dana dikembalikan");
      load();
    } catch (err) {
      toast.error(errMsg(err, "Gagal memproses"));
    } finally {
      setBusy("");
    }
  };

  const gw = overview?.gateway;
  const lg = overview?.ledger;

  return (
    <div className="max-w-3xl mx-auto space-y-5">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-2xl font-semibold flex items-center gap-2">
            <Wrench className="h-6 w-6 text-primary" /> Alat Developer
          </h1>
          <p className="text-sm text-muted-foreground">Status gateway, integritas ledger, dan kontrol sandbox.</p>
        </div>
        <Button variant="outline" size="sm" onClick={load} data-testid="devtools-refresh-button">
          <RefreshCw className="h-4 w-4 mr-1.5" /> Muat Ulang
        </Button>
      </div>

      {/* Gateway status */}
      <Card className="rounded-2xl" data-testid="devtools-gateway-card">
        <CardContent className="p-5">
          <div className="flex items-center gap-2 mb-4">
            <Server className="h-4 w-4 text-primary" />
            <h3 className="font-heading font-semibold">Payment Gateway</h3>
            {gw && (
              <Badge variant="outline" className={gw.provider === "xendit" ? "bg-blue-50 text-primary border-blue-100" : "bg-secondary"}>
                {gw.provider === "xendit" ? "Xendit Sandbox (Hybrid)" : "Simulasi Internal"}
              </Badge>
            )}
          </div>
          {!overview ? (
            <Skeleton className="h-24 w-full rounded-xl" />
          ) : (
            <div className="space-y-3 text-sm">
              <div className="flex justify-between gap-3">
                <span className="text-muted-foreground">API Key</span>
                <span className="font-mono text-xs">{gw.xendit_key_configured ? gw.xendit_key_prefix : "Tidak dikonfigurasi"}</span>
              </div>
              <div className="flex justify-between gap-3 items-center">
                <span className="text-muted-foreground">Autentikasi (read)</span>
                {gw.auth_probe?.ok ? (
                  <Badge variant="outline" className="bg-emerald-50 text-emerald-600 border-emerald-100"><CheckCircle2 className="h-3 w-3 mr-1" /> OK</Badge>
                ) : (
                  <Badge variant="outline" className="bg-red-50 text-red-600 border-red-100"><XCircle className="h-3 w-3 mr-1" /> Gagal</Badge>
                )}
              </div>
              {gw.auth_probe?.message && <p className="text-xs text-muted-foreground">{gw.auth_probe.message}</p>}
              {gw.last_success_at && (
                <div className="flex justify-between gap-3">
                  <span className="text-muted-foreground">Sukses terakhir</span>
                  <span className="text-xs">{formatDateTime(gw.last_success_at)}</span>
                </div>
              )}
              {gw.last_error && (
                <div className="rounded-xl border border-teal-200 bg-teal-50 p-3" data-testid="devtools-gateway-error">
                  <p className="text-xs font-semibold text-[#0FA5A0] flex items-center gap-1 mb-1">
                    <AlertTriangle className="h-3.5 w-3.5" /> Error terakhir (fallback ke simulasi)
                  </p>
                  <p className="text-xs text-muted-foreground break-all">{gw.last_error}</p>
                  <p className="text-xs text-muted-foreground mt-2">
                    Jika error 403: buka <span className="font-semibold">dashboard.xendit.co → Settings → API Keys</span>, buat key baru dengan izin <span className="font-semibold">Money-in: Write</span> dan <span className="font-semibold">Money-out: Write</span>, lalu perbarui XENDIT_SECRET_KEY.
                  </p>
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Ledger integrity */}
      <Card className="rounded-2xl" data-testid="devtools-ledger-card">
        <CardContent className="p-5">
          <div className="flex items-center gap-2 mb-4">
            <Scale className="h-4 w-4 text-primary" />
            <h3 className="font-heading font-semibold">Integritas Ledger</h3>
            {lg && (
              <Badge variant="outline" className={lg.balanced ? "bg-emerald-50 text-emerald-600 border-emerald-100" : "bg-red-50 text-red-600 border-red-100"}>
                {lg.balanced ? "Seimbang" : "TIDAK SEIMBANG"}
              </Badge>
            )}
          </div>
          {!overview ? (
            <Skeleton className="h-20 w-full rounded-xl" />
          ) : (
            <div className="grid grid-cols-2 gap-3 text-sm">
              <div className="rounded-xl bg-secondary p-3">
                <p className="text-xs text-muted-foreground">Total entri ledger (global)</p>
                <p className="font-semibold tabular-nums">{lg.entries_count} entri · Σ = {lg.global_sum}</p>
              </div>
              <div className="rounded-xl bg-secondary p-3">
                <p className="text-xs text-muted-foreground">Saldo kamu vs posisi ledger</p>
                <p className="font-semibold tabular-nums">
                  {formatRupiah(lg.my_wallet_balance)} {lg.my_reconciled ? "= " : "≠ "}{formatRupiah(lg.my_ledger_balance)}
                </p>
              </div>
              <div className="rounded-xl bg-secondary p-3">
                <p className="text-xs text-muted-foreground">Transaksi kamu</p>
                <p className="font-semibold tabular-nums">{overview.counts.my_transactions}</p>
              </div>
              <div className="rounded-xl bg-secondary p-3">
                <p className="text-xs text-muted-foreground">VA · QRIS kamu</p>
                <p className="font-semibold tabular-nums">{overview.counts.virtual_accounts} · {overview.counts.qris_codes}</p>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Pending transfers */}
      <Card className="rounded-2xl" data-testid="devtools-pending-card">
        <CardContent className="p-5">
          <div className="flex items-center gap-2 mb-4">
            <Clock className="h-4 w-4 text-amber-500" />
            <h3 className="font-heading font-semibold">Transfer Pending</h3>
            {pending && <Badge variant="outline">{pending.length}</Badge>}
          </div>
          {!pending ? (
            <Skeleton className="h-16 w-full rounded-xl" />
          ) : pending.length === 0 ? (
            <p className="text-sm text-muted-foreground" data-testid="devtools-no-pending">Tidak ada transfer pending saat ini.</p>
          ) : (
            <div className="space-y-3">
              {pending.map((t) => (
                <div key={t.id} className="rounded-xl border p-3" data-testid="devtools-pending-row">
                  <div className="flex items-center justify-between gap-2 mb-2">
                    <div className="min-w-0">
                      <p className="text-sm font-semibold truncate">{t.account_holder || t.bank_name} · {formatRupiah(t.amount)}</p>
                      <p className="text-xs text-muted-foreground font-mono">{t.reference} · {t.bank_name} {maskAccount(t.account_number)} · jalur: {t.gateway || "simulated"}</p>
                    </div>
                  </div>
                  <Separator className="my-2" />
                  <div className="flex gap-2">
                    <Button size="sm" className="flex-1 bg-emerald-600 hover:bg-emerald-700" disabled={busy === t.id + "SUCCESS"} onClick={() => settle(t.id, "SUCCESS")} data-testid="devtools-settle-success-button">
                      Selesaikan (Sukses)
                    </Button>
                    <Button size="sm" variant="outline" className="flex-1 border-destructive/40 text-destructive hover:bg-destructive/5" disabled={busy === t.id + "FAILED"} onClick={() => settle(t.id, "FAILED")} data-testid="devtools-settle-fail-button">
                      Gagalkan (Refund)
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
